export const EDUCATION_TEMPLATE_STYLES = Object.freeze({
  DEFAULT: 'default',
  EXAM: 'exam'
});

/**
 * Exam Portal theme tokens, taken from the approved design (index.html) so that the
 * FUNCTIONAL education routes — attendance, myCourses, profile, exam-management and the rest,
 * which render live components rather than template sections — pick up the same palette as the
 * designed template pages. These feed `--education-exam-*` CSS variables via
 * EducationThemeBridge, which EducationExamTemplate.css consumes.
 *
 * `accent` is deliberately the mid-teal rather than the design's lime (#d8ff91): the lime is a
 * decorative colour on dark panels and fails contrast as text/border on the light surfaces the
 * live components use.
 */
export const EDUCATION_EXAM_THEME_DEFAULTS = Object.freeze({
  primary: '#1f5a54',
  secondary: '#2a9a85',
  accent: '#2b8b77',
  lime: '#d8ff91',
  background: '#f4f0eb',
  surface: '#ffffff',
  color: '#171918',
  mutedColor: '#66716e',
  borderColor: '#dfe4e0',
  successColor: '#16a34a',
  warningColor: '#ffb536',
  errorColor: '#dc2626',
  cardShadow: '0 18px 45px rgba(23, 63, 61, 0.10)',
  footerBackground: '#112f2d'
});

const normalizeStyle = (value) => (
  String(value || '').trim().toLowerCase() === EDUCATION_TEMPLATE_STYLES.EXAM
    ? EDUCATION_TEMPLATE_STYLES.EXAM
    : EDUCATION_TEMPLATE_STYLES.DEFAULT
);

export const getEducationTemplateStyle = (template = {}) => {
  const configuredStyle = template?.attributes?.educationTemplateStyle;
  if (configuredStyle) return normalizeStyle(configuredStyle);

  const sourceIdentity = [
    template?.baseTemplateKey,
    template?.attributes?.sourceTemplate,
    template?.attributes?.sourceComponent,
    ...(Array.isArray(template?.pages)
      ? template.pages.flatMap((page) => (
          Array.isArray(page?.sections)
            ? page.sections.flatMap((section) => [
                section?.bindings?.sourceComponent,
                section?.content?.sourceComponent,
                section?.bindings?.componentKey,
                section?.content?.componentKey
              ])
            : []
        ))
      : [])
  ]
    .filter(Boolean)
    .join(' ');
  const keyIdentity = [template?.templateKey, template?.baseTemplateKey]
    .filter(Boolean)
    .join(' ');

  return /\bEducationExamTemplate\b/i.test(sourceIdentity)
    || /(^|[\s_-])(EDUCATION_EXAM|EDU_EXAM)([\s_-]|$)/i.test(keyIdentity)
    ? EDUCATION_TEMPLATE_STYLES.EXAM
    : EDUCATION_TEMPLATE_STYLES.DEFAULT;
};

export const isEducationExamTemplate = (template = {}) => (
  getEducationTemplateStyle(template) === EDUCATION_TEMPLATE_STYLES.EXAM
);

export const withEducationTemplateStyle = (template = {}, style) => ({
  ...template,
  attributes: {
    ...(template.attributes || {}),
    educationTemplateStyle: normalizeStyle(style)
  }
});
